import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Chart from 'chart.js/auto';
import apiClient from '../services/api';
import './TicksPage.css';

interface TickLog {
  id: number;
  created_at: string;
  operation_type: string;
  amount: number;
  description: string;
  report_id?: number;
  report_title?: string;
}

interface DailyUsage {
  date: string;
  ticks: number;
}

interface ProfileStats {
  balance: number;
  total_reports: number;
  total_debits: number;
  total_credits: number;
  daily_usage: DailyUsage[];
}

const TicksPage: React.FC = () => {
  const { t, i18n } = useTranslation('profile');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user } = useAuth();
  const [tickLogs, setTickLogs] = useState<TickLog[]>([]);
  const [stats, setStats] = useState<ProfileStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch tick logs
        const logsResponse = await apiClient.get('/profile/tick-logs');
        const logsData = logsResponse.data;
        setTickLogs(logsData);
        
        // Fetch usage statistics
        const statsResponse = await apiClient.get('/profile/stats');
        const statsData = statsResponse.data;
        setStats(statsData);
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(t('ticks.error_loading'));
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  useEffect(() => {
    // Initialize chart when stats are loaded
    if (stats && chartRef.current) {
      // Destroy previous chart instance if it exists
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
      
      const ctx = chartRef.current.getContext('2d');
      if (ctx) {
        // Format dates for display
        const formatDate = (dateStr: string) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const [year, month, day] = dateStr.split('-');
          return `${day}.${month}`;
        };
        
        // Create new chart
        chartInstance.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: stats.daily_usage.map(item => formatDate(item.date)),
            datasets: [{
              label: t('ticks.chart_dataset'),
              data: stats.daily_usage.map(item => item.ticks),
              backgroundColor: 'rgba(54, 162, 235, 0.2)',
              borderColor: 'rgba(54, 162, 235, 1)',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: t('ticks.chart_y_label')
                }
              },
              x: {
                title: {
                  display: true,
                  text: t('ticks.chart_x_label')
                }
              }
            },
            plugins: {
              title: {
                display: true,
                text: t('ticks.chart_title')
              },
              legend: {
                display: true,
                position: 'top'
              },
              tooltip: {
                callbacks: {
                  title: function(tooltipItems) {
                    return tooltipItems[0].label;
                  },
                  label: function(context) {
                    return t('ticks.chart_tooltip', { count: context.raw as number });
                  }
                }
              }
            }
          }
        });
      }
    }
    
    // Cleanup function to destroy chart when component unmounts
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [stats]);

  if (isLoading) {
    return (
      <div className="main-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('ticks.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-container">
        <div className="error-container">
          <h2>{t('ticks.error_loading')}</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <section className="usage-section">
        <div className="usage-header">
          <h2>{t('ticks.title')}</h2>
          <Link to="/payment" className="yoomoney-button">
            <i className="fas fa-wallet"></i>
            {t('ticks.top_up_button')}
          </Link>
        </div>
        
        {/* График использования тиков */}
        <div className="usage-chart-container">
          <canvas ref={chartRef}></canvas>
        </div>

        {/* Таблица с историей операций */}
        <div className="usage-table">
          <table>
            <thead>
              <tr>
                <th>{t('ticks.table_date')}</th>
                <th>{t('ticks.table_operation')}</th>
                <th>{t('ticks.table_amount')}</th>
                <th>{t('ticks.table_description')}</th>
                <th>{t('ticks.table_report')}</th>
              </tr>
            </thead>
            <tbody>
              {tickLogs.length > 0 ? (
                tickLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{new Date(log.created_at).toLocaleString(i18n.language === 'en' ? 'en-US' : 'ru-RU', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</td>
                    <td>
                      {log.operation_type === 'credit' ? (
                        <span className="operation-credit">{t('ticks.type_credit')}</span>
                      ) : log.operation_type === 'debit' ? (
                        <span className="operation-debit">{t('ticks.type_debit')}</span>
                      ) : (
                        <span className="operation-other">{log.operation_type}</span>
                      )}
                    </td>
                    <td>
                      {log.amount > 0 ? (
                        <span className="amount-positive">+{log.amount}</span>
                      ) : (
                        <span className="amount-negative">{log.amount}</span>
                      )}
                    </td>
                    <td>{log.description}</td>
                    <td>
                      {log.report_id ? (
                        <Link 
                          to={`/reports/${log.report_id}`} 
                          className="report-link"
                        >
                          {log.report_title || t('ticks.report_fallback', { id: log.report_id })}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="no-data">
                    {t('ticks.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default TicksPage;
